import {
  WorkerBullQueueCounts,
  WorkerConnectionEvent,
  WorkerConnectionStatus,
  WorkerProcessConfig as ApiWorkerProcessConfig,
  WorkerStatus,
} from 'api/dod/dod.v2.types';
import { DodStateName } from '../components/stateChip/StateChip.types';

/**
 * Pure helpers for the RCP worker tab.
 *
 * The heartbeat document is the only thing the backend can see of the worker -- there is no HTTP
 * channel between them (design §3.3) -- so this file never invents a field. The shapes below are
 * the wire types from `api/dod/dod.v2.types`, loosened where a heartbeat published by an older
 * worker may lack a member: `config` is optional as a whole and every member inside it is
 * optional, so a missing value reads as "unknown", never as "disabled".
 */

export type WorkerBullCounts = WorkerBullQueueCounts;

export type WorkerConnection = WorkerConnectionStatus;

export type WorkerProcessConfig = Partial<ApiWorkerProcessConfig>;

export type WorkerProcessStatus = Omit<WorkerStatus, 'config' | 'connections'> & {
  config?: WorkerProcessConfig;
  connections: WorkerConnection[];
};

export type WorkerEvent = WorkerConnectionEvent;

/** Circuit-breaker failure budget; the worker opens the breaker on the third consecutive failure. */
export const WORKER_CIRCUIT_MAX_FAILURES = 3;

/** Below this many days the certificate chip turns amber (design §4.6). */
export const CERT_WARNING_DAYS = 30;

/** A heartbeat published more than this long ago makes the whole card suspect. */
export const WORKER_STALE_AFTER_MS = 60_000;

const STATE_NAMES: DodStateName[] = ['connected', 'disconnected', 'reconnecting', 'error'];

/**
 * The heartbeat carries `state` as a free-form string, because the worker's connection manager owns
 * that vocabulary. Anything the chip does not know renders as "unknown" rather than as an error.
 */
export const workerConnectionState = (state: string): DodStateName =>
  STATE_NAMES.includes(state as DodStateName) ? (state as DodStateName) : 'unknown';

export const workerCircuitState = (circuit: string): DodStateName =>
  circuit === 'open' || circuit === 'closed' || circuit === 'half_open' ? (circuit as DodStateName) : 'unknown';

/** Coarse, human duration: seconds under a minute, then minutes, hours, days. */
export const formatDurationMs = (milliseconds: number | null | undefined): string => {
  if (milliseconds === null || milliseconds === undefined || !Number.isFinite(milliseconds) || milliseconds < 0) {
    return '—';
  }

  const seconds = Math.floor(milliseconds / 1000);

  if (seconds < 60) {
    return `${seconds} s`;
  }

  const minutes = Math.floor(seconds / 60);

  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return minutes % 60 === 0 ? `${hours} h` : `${hours} h ${minutes % 60} min`;
  }

  const days = Math.floor(hours / 24);

  return hours % 24 === 0 ? `${days} d` : `${days} d ${hours % 24} h`;
};

/** Age of an ISO timestamp against `now`; null when the timestamp is absent or unparseable. */
export const ageMsSince = (isoTimestamp: string | null | undefined, now: number): number | null => {
  if (!isoTimestamp) {
    return null;
  }

  const parsed = Date.parse(isoTimestamp);

  return Number.isNaN(parsed) ? null : Math.max(0, now - parsed);
};

export const formatBytes = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) {
    return '—';
  }

  const mib = bytes / (1024 * 1024);

  return mib >= 1024 ? `${(mib / 1024).toFixed(1)} GiB` : `${Math.round(mib)} MiB`;
};

/** Fill ratio for the DB-pool and memory bars, clamped so a bad sample cannot overflow the track. */
export const fillRatio = (used: number | null | undefined, max: number | null | undefined): number => {
  if (!used || !max || max <= 0) {
    return 0;
  }

  return Math.min(1, Math.max(0, used / max));
};

export const certDaysLeft = (
  connection: Pick<WorkerConnection, 'certDaysLeft' | 'certExpiresAt'>,
  now: number,
): number | null => {
  if (connection.certDaysLeft !== null && connection.certDaysLeft !== undefined) {
    return connection.certDaysLeft;
  }

  if (!connection.certExpiresAt) {
    return null;
  }

  const parsed = Date.parse(connection.certExpiresAt);

  return Number.isNaN(parsed) ? null : Math.floor((parsed - now) / 86_400_000);
};

export const isCertExpiringSoon = (daysLeft: number | null): boolean =>
  daysLeft !== null && daysLeft < CERT_WARNING_DAYS;

/** Bull's `Record<queue, counts>` as a list, in a stable order so the cards do not reshuffle. */
export const bullQueueList = (
  bull: Record<string, WorkerBullCounts> | undefined,
): Array<{ queue: string } & WorkerBullCounts> =>
  Object.entries(bull ?? {})
    .map(([queue, counts]) => ({ queue, ...counts }))
    .sort((left, right) => left.queue.localeCompare(right.queue));

export type WorkerEventTone = 'ok' | 'warn' | 'bad' | 'neutral';

/**
 * Tone of a connection event. The vocabulary is the worker's RCP_CONNECTION_EVENTS, matched by
 * substring so a renamed-but-related event ("rcp.connection.circuit_open") still lands correctly.
 */
export const workerEventTone = (event: string): WorkerEventTone => {
  const name = event.toLowerCase();

  if (name.includes('circuit_open') || name.includes('disconnected') || name.includes('error')) {
    return 'bad';
  }

  if (name.includes('half_open') || name.includes('reconnect')) {
    return 'warn';
  }

  if (name.includes('connected') || name.includes('circuit_closed')) {
    return 'ok';
  }

  return 'neutral';
};

/**
 * The state chip word for a connection event, so the list shows "połączony" / "rozłączony" /
 * "łączenie ponowne" instead of the wire name. Substring match like workerEventTone.
 */
export const workerEventState = (event: string): DodStateName => {
  const name = event.toLowerCase();

  if (name.includes('circuit_open')) return 'open';
  if (name.includes('circuit_closed')) return 'closed';
  if (name.includes('half_open')) return 'half_open';
  if (name.includes('disconnected')) return 'disconnected';
  if (name.includes('reconnect')) return 'reconnecting';
  if (name.includes('connected')) return 'connected';
  if (name.includes('error')) return 'error';

  return 'unknown';
};

export type ReconnectMarker = {
  id: string;
  /** 0..1 position along the timeline; 0 is `from`, 1 is `to`. */
  position: number;
  tone: WorkerEventTone;
  event: string;
  at: number;
  detail: string | null;
};

/**
 * Events placed on the 24 h reconnect timeline.
 *
 * Events outside the window are dropped rather than clamped to an edge: a marker pinned at "-24h"
 * that actually happened three days ago is a lie the operator cannot see through.
 */
export const reconnectMarkers = (events: WorkerEvent[], from: number, to: number): ReconnectMarker[] => {
  const span = to - from;

  if (span <= 0) {
    return [];
  }

  return events
    .map((event, index) => ({ event, index, at: Date.parse(event.at) }))
    .filter(({ at }) => Number.isFinite(at) && at >= from && at <= to)
    .map(({ event, index, at }) => ({
      id: `${event.rcpId}-${at}-${index}`,
      position: (at - from) / span,
      tone: workerEventTone(event.event),
      event: event.event,
      at,
      detail: event.detail,
    }))
    .sort((left, right) => left.at - right.at);
};

/** Events belonging to one RCP server, newest first. */
export const eventsForConnection = (events: WorkerEvent[], rcpId: string): WorkerEvent[] =>
  events
    .filter((event) => event.rcpId === rcpId)
    .slice()
    .sort((left, right) => Date.parse(right.at) - Date.parse(left.at));

/** Placeholder for a value the heartbeat does not carry. Never an empty string, never a zero. */
export const UNKNOWN_VALUE = '—';

/**
 * Bull's rate limiter as "20/s", or "20 / 500 ms" when the window is not a whole second.
 *
 * A limiter that is configured off is different from one nobody reported: the first returns null
 * from the worker and prints a dash, the second never reaches this function at all.
 */
export const formatLimiter = (max: number | null | undefined, durationMs: number | null | undefined): string => {
  if (!max || !durationMs || durationMs <= 0) {
    return UNKNOWN_VALUE;
  }

  return durationMs === 1000 ? `${max}/s` : `${max} / ${durationMs} ms`;
};

/**
 * Sweep period. `null` is meaningful here -- the worker says the sweep is DISABLED -- and is
 * reported as such, while an absent config prints the unknown dash.
 */
export const formatSweepInterval = (config: WorkerProcessConfig | undefined, disabledLabel: string): string => {
  if (!config || !('sweepIntervalMs' in config)) {
    return UNKNOWN_VALUE;
  }

  return config.sweepIntervalMs === null || config.sweepIntervalMs === undefined
    ? disabledLabel
    : formatDurationMs(config.sweepIntervalMs);
};

/** Handshake duration in ms, or the unknown dash before the first handshake. */
export const formatHandshake = (handshakeMs: number | null | undefined): string =>
  handshakeMs === null || handshakeMs === undefined || !Number.isFinite(handshakeMs)
    ? UNKNOWN_VALUE
    : `${Math.round(handshakeMs)} ms`;

/**
 * The monospace identity line under a connection's name: endpoint, TLS, certificate CN, session.
 *
 * Absent parts are dropped rather than printed as dashes -- a line reading "— · — · sesja 18 min"
 * is noise, while an omitted part is simply not yet known.
 */
export const connectionIdentityParts = (connection: WorkerConnection, sessionLabel: string | null): string[] => {
  const tls = connection.tlsVersion
    ? [
        connection.tlsVersion,
        connection.handshakeMs === null || connection.handshakeMs === undefined
          ? ''
          : formatHandshake(connection.handshakeMs),
      ]
        .filter(Boolean)
        .join(' ')
    : '';

  return [
    formatEndpoint(connection),
    tls,
    connection.certSubjectCn ? `CN=${connection.certSubjectCn}` : '',
    sessionLabel ?? '',
  ].filter(Boolean);
};

/** `host:port`, or just the host, or nothing at all -- never "null:null". */
export const formatEndpoint = (connection: Pick<WorkerConnection, 'host' | 'port'>): string => {
  if (!connection.host) {
    return '';
  }

  return connection.port ? `${connection.host}:${connection.port}` : connection.host;
};
